import { DOKUMENT_ARTEN, type DokumentArt } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AblageBaum } from '../components/ablage/AblageBaum'
import { DokumentAnsicht } from '../components/ablage/DokumentAnsicht'
import { Topbar } from '../components/Topbar'
import { Button, Card, CardPad } from '../components/ui/primitives'
import { pfadZu } from '../lib/ablage'
import { anhangHochladen } from '../lib/anhang'
import { ablageQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **Die Ablage** ([Z-14](../../../../docs/phasen/zusatz-14-ablage.md)) – Vorlagen, Regeln und
 * Dateien einer Arbeitswelt, als Baum mit Fassungen.
 *
 * Nils, 11.09.2026: *„so eine Art «Daten-Tank» … Templates (MD) mit Versionierung und
 * Hierarchie – Main-Dev.md, darunter die Projekt.md … Aber auch Bilder … Bei Marketing wären
 * das bspw. Design CI.pdf."*
 *
 * Links der Baum, rechts das gewählte Dokument – wie ein Dateimanager, nur dass jeder Knoten
 * selbst ein Text sein kann. `?dokument=<Kennung>` öffnet ein bestimmtes, damit man einen
 * Verweis auf `Main-Dev.md` in einen Kommentar setzen kann.
 *
 * **Anlegen** geht über ein Formular oben rechts, nicht über einen Rechtsklick: Rechtsklick gibt
 * es am Tablet nicht, und ein Formular sagt, *wo* das Neue landet („unter «Main-Dev»").
 */
export function Ablage() {
  const queryClient = useQueryClient()
  const { data: eintraege = [], isLoading } = useQuery(ablageQuery)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const [neuOffen, setNeuOffen] = useState(false)

  /** Einmal beim Laden – wie bei `?vorhaben=`. */
  useEffect(() => {
    const gewuenscht = new URLSearchParams(window.location.search).get('dokument')
    if (gewuenscht) setGewaehlt(gewuenscht)
  }, [])

  const waehlen = (id: string | null) => {
    setGewaehlt(id)
    const adresse = new URL(window.location.href)
    if (id) adresse.searchParams.set('dokument', id)
    else adresse.searchParams.delete('dokument')
    window.history.replaceState(null, '', adresse)
  }

  const dokument = eintraege.find((e) => e.id === gewaehlt) ?? null

  return (
    <>
      <Topbar
        titel="Ablage"
        untertitel="Vorlagen, Regeln und Dateien dieser Arbeitswelt – mit Fassungen"
        aktion={
          <Button variant="primary" size="sm" onClick={() => setNeuOffen((v) => !v)}>
            <Plus className="mr-1 inline size-3.5" aria-hidden />
            Neu
          </Button>
        }
      />

      {neuOffen && (
        <NeuFormular
          eintraege={eintraege}
          vorgeschlagenUnter={
            dokument?.art === 'ordner' || dokument?.art === 'markdown'
              ? dokument.id
              : (dokument?.elternId ?? null)
          }
          fertig={(id) => {
            setNeuOffen(false)
            if (id) waehlen(id)
            void queryClient.invalidateQueries({ queryKey: ['dokumente'] })
          }}
        />
      )}

      {isLoading && <div className="text-[13px] text-ink2">Lädt …</div>}

      {!isLoading && (
        <div className="grid items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <Card>
            <div className="p-2">
              <AblageBaum eintraege={eintraege} gewaehlt={gewaehlt} waehlen={waehlen} />
            </div>
          </Card>
          <Card className="min-w-0">
            <CardPad>
              {dokument ? (
                <DokumentAnsicht
                  dokument={dokument}
                  alle={eintraege}
                  waehlen={waehlen}
                  nachLoeschen={() => waehlen(null)}
                />
              ) : eintraege.length === 0 ? (
                <div className="text-[13px] text-ink2">
                  <div className="font-semibold">Die Ablage ist noch leer.</div>
                  <p className="mt-1.5 text-xs">
                    Hier liegt, was für diese Arbeitswelt gilt: Regeln, Vorlagen, das CI-Handbuch.
                    Ein Text kann weitere Texte unter sich haben – oben, was überall gilt, darunter,
                    was ein Projekt konkretisiert. „Mit Übergeordneten kopieren" holt dann alles in
                    einer Datei.
                  </p>
                </div>
              ) : (
                <div className="text-xs text-muted">Links ein Dokument wählen.</div>
              )}
            </CardPad>
          </Card>
        </div>
      )}
    </>
  )
}

const ART_LABEL: Record<DokumentArt, string> = {
  markdown: 'Text (Markdown)',
  datei: 'Datei (Bild, PDF, …)',
  ordner: 'Ordner',
}

/**
 * **Anlegen** – Titel, Art, Ort. Eine Datei braucht keinen Titel: sie heißt wie die Datei.
 *
 * Bei einer Datei passiert zweierlei: erst der Platzhalter (`anlegen`), dann der Upload an
 * ihn. Scheitert der Upload, wird der Platzhalter wieder entfernt – sonst stünde links ein
 * Dokument ohne Fassung, und niemand wüsste, warum.
 */
function NeuFormular({
  eintraege,
  vorgeschlagenUnter,
  fertig,
}: {
  eintraege: readonly { id: string; elternId: string | null; titel: string; sortOrder: number }[]
  vorgeschlagenUnter: string | null
  fertig: (id: string | null) => void
}) {
  const [titel, setTitel] = useState('')
  const [art, setArt] = useState<DokumentArt>('markdown')
  const [unter, setUnter] = useState<string>(vorgeschlagenUnter ?? '')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const dateiwahl = useRef<HTMLInputElement>(null)

  const anlegen = useMutation({
    mutationFn: (eingabe: { titel: string; art: DokumentArt; elternId: string | null }) =>
      trpc.dokumente.anlegen.mutate(eingabe),
  })

  async function absenden(datei?: File): Promise<void> {
    setFehler(null)
    const name = datei ? datei.name : titel.trim()
    if (!name) {
      setFehler('Ein Titel fehlt.')
      return
    }
    setLaeuft(true)
    try {
      const { id } = await anlegen.mutateAsync({ titel: name, art, elternId: unter || null })
      if (datei) {
        try {
          await anhangHochladen({ dokumentId: id }, datei)
        } catch (e) {
          await trpc.dokumente.loeschen.mutate({ id }).catch(() => undefined)
          throw e
        }
      }
      fertig(id)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Card className="mb-4">
      <CardPad>
        <form
          className="flex flex-wrap items-end gap-3 text-xs"
          onSubmit={(e) => {
            e.preventDefault()
            if (art === 'datei') dateiwahl.current?.click()
            else void absenden()
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-ink2">Art</span>
            <select
              value={art}
              onChange={(e) => setArt(e.target.value as DokumentArt)}
              className="rounded-lg border border-line2 px-2 py-1.5"
            >
              {DOKUMENT_ARTEN.map((a) => (
                <option key={a} value={a}>
                  {ART_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
          {art !== 'datei' && (
            <label className="flex min-w-[220px] flex-1 flex-col gap-1">
              <span className="text-ink2">Titel</span>
              <input
                autoFocus
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                placeholder={art === 'ordner' ? 'z. B. Design-Richtlinien' : 'z. B. Main-Dev'}
                className="rounded-lg border border-line2 px-2 py-1.5 text-[13px]"
              />
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-ink2">Unter</span>
            <select
              value={unter}
              onChange={(e) => setUnter(e.target.value)}
              className="max-w-[260px] rounded-lg border border-line2 px-2 py-1.5"
            >
              <option value="">oben</option>
              {eintraege.map((e) => (
                <option key={e.id} value={e.id}>
                  {pfadZu(eintraege, e.id)
                    .map((p) => p.titel)
                    .join(' › ')}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="primary" size="sm" disabled={laeuft}>
            {laeuft ? 'Legt an …' : art === 'datei' ? 'Datei wählen und anlegen' : 'Anlegen'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => fertig(null)}>
            Abbrechen
          </Button>
          <input
            ref={dateiwahl}
            type="file"
            hidden
            aria-label="Datei"
            onChange={(e) => {
              const datei = e.target.files?.[0]
              e.target.value = ''
              if (datei) void absenden(datei)
            }}
          />
          {fehler && (
            <div role="alert" className="w-full text-[#b42318]">
              {fehler}
            </div>
          )}
        </form>
      </CardPad>
    </Card>
  )
}
